import React, { useRef, useState } from 'react';
import { parsePayoutsFromCSV, downloadCSVTemplate, type CsvImportResult } from '../../utils/csv';
import { usePayoutMutations } from '../../utils/hooks/usePayoutMutations';
import type { Payout } from '../../types/payout';

interface ImportCSVModalProps {
  isOpen: boolean;
  onClose: () => void;
  existingPayouts: Payout[];
}

type Step = 'select' | 'preview' | 'importing' | 'done' | 'error';

export const ImportCSVModal: React.FC<ImportCSVModalProps> = ({
  isOpen,
  onClose,
  existingPayouts,
}) => {
  const fileRef = useRef<HTMLInputElement>(null);
  const [step, setStep] = useState<Step>('select');
  const [parseResult, setParseResult] = useState<CsvImportResult | null>(null);
  const [parseError, setParseError] = useState<string | null>(null);
  const [importedCount, setImportedCount] = useState(0);
  const [fileName, setFileName] = useState<string | null>(null);
  const { bulkAddPayouts } = usePayoutMutations();

  const reset = () => {
    setStep('select');
    setParseResult(null);
    setParseError(null);
    setImportedCount(0);
    setFileName(null);
    if (fileRef.current) fileRef.current.value = '';
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setParseError(null);
    try {
      const text = await file.text();
      const result = parsePayoutsFromCSV(text, existingPayouts);
      setParseResult(result);
      setFileName(file.name);
      setStep('preview');
    } catch (err) {
      setParseError(err instanceof Error ? err.message : 'Erreur lors de la lecture du fichier');
      setStep('error');
    }
  };

  const handleImport = async () => {
    if (!parseResult || parseResult.toImport.length === 0) return;
    setStep('importing');
    try {
      const label = fileName ?? `Import du ${new Date().toLocaleDateString('fr-FR')}`;
      await bulkAddPayouts(parseResult.toImport, label);
      setImportedCount(parseResult.toImport.length);
      setStep('done');
    } catch (err) {
      setParseError(err instanceof Error ? err.message : 'Erreur lors de l\'import');
      setStep('error');
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black/40 z-40" onClick={handleClose} />
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-lg">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <div>
              <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100">Importer des transactions</h2>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">Format CSV exportable depuis CryptoTrack</p>
            </div>
            <button
              onClick={handleClose}
              className="p-2 hover:bg-gray-100 hover:dark:bg-gray-700 rounded-lg transition-colors"
            >
              <svg className="w-5 h-5 text-gray-500 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Body */}
          <div className="px-6 py-6">
            {step === 'select' && (
              <div className="text-center">
                <div className="mb-4 flex justify-center">
                  <div className="w-16 h-16 bg-blue-50 rounded-full flex items-center justify-center">
                    <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                    </svg>
                  </div>
                </div>
                <p className="text-gray-600 dark:text-gray-400 text-sm mb-4">
                  Sélectionnez un fichier CSV au format <strong>CryptoTrack importable</strong>.<br />
                  Les doublons seront automatiquement détectés et ignorés.
                </p>
                <button
                  onClick={downloadCSVTemplate}
                  className="inline-flex items-center gap-1.5 text-sm text-blue-600 hover:text-blue-800 underline underline-offset-2 mb-6 transition-colors"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3M3 17V7a2 2 0 012-2h6l2 2h6a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
                  </svg>
                  Télécharger le modèle CSV
                </button>
                <div className="flex justify-center">
                <label className="cursor-pointer inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-medium px-5 py-2.5 rounded-lg transition-colors">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                  </svg>
                  Choisir un fichier CSV
                  <input
                    ref={fileRef}
                    type="file"
                    accept=".csv,text/csv"
                    className="hidden"
                    onChange={handleFileChange}
                  />
                </label>
                </div>
              </div>
            )}

            {step === 'preview' && parseResult && (
              <div>
                <div className="grid grid-cols-3 gap-3 mb-6">
                  <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-center">
                    <div className="text-2xl font-bold text-green-700">{parseResult.toImport.length}</div>
                    <div className="text-xs text-green-600 mt-1">À importer</div>
                  </div>
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-center">
                    <div className="text-2xl font-bold text-yellow-700">{parseResult.duplicates.length}</div>
                    <div className="text-xs text-yellow-600 mt-1">Doublons ignorés</div>
                  </div>
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-center">
                    <div className="text-2xl font-bold text-red-700">{parseResult.invalid}</div>
                    <div className="text-xs text-red-600 mt-1">Lignes invalides</div>
                  </div>
                </div>

                {parseResult.toImport.length === 0 ? (
                  <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-2">
                    Aucune nouvelle transaction à importer.
                  </p>
                ) : (
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    <strong>{parseResult.toImport.length}</strong> transaction(s) seront ajoutées à votre historique.
                  </p>
                )}
              </div>
            )}

            {step === 'importing' && (
              <div className="text-center py-4">
                <div className="w-10 h-10 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
                <p className="text-gray-600 dark:text-gray-400 text-sm">Import en cours...</p>
              </div>
            )}

            {step === 'done' && (
              <div className="text-center py-2">
                <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <svg className="w-7 h-7 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <p className="font-semibold text-gray-900 dark:text-gray-100">Import réussi</p>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                  {importedCount} transaction(s) importée(s) avec succès.
                </p>
              </div>
            )}

            {step === 'error' && (
              <div className="text-center py-2">
                <div className="w-14 h-14 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <svg className="w-7 h-7 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </div>
                <p className="font-semibold text-gray-900 dark:text-gray-100">Erreur</p>
                <p className="text-sm text-red-600 mt-1">{parseError}</p>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-3">
            {step === 'select' && (
              <button
                onClick={handleClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 hover:dark:bg-gray-700 rounded-lg transition-colors"
              >
                Annuler
              </button>
            )}

            {step === 'preview' && (
              <>
                <button
                  onClick={reset}
                  className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 hover:dark:bg-gray-700 rounded-lg transition-colors"
                >
                  Changer de fichier
                </button>
                <button
                  onClick={handleImport}
                  disabled={parseResult?.toImport.length === 0}
                  className="px-5 py-2 text-sm font-medium bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300 text-white rounded-lg transition-colors"
                >
                  Importer {parseResult?.toImport.length} transaction(s)
                </button>
              </>
            )}

            {(step === 'done' || step === 'error') && (
              <>
                {step === 'error' && (
                  <button
                    onClick={reset}
                    className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 hover:dark:bg-gray-700 rounded-lg transition-colors"
                  >
                    Réessayer
                  </button>
                )}
                <button
                  onClick={handleClose}
                  className="px-5 py-2 text-sm font-medium bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
                >
                  Fermer
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
};
